import { AnyAction } from 'redux';
import { STATE } from 'app/constants';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching, ComplexValue } from 'app/types-shared';
import { complexValidationFromServer } from 'app/infrastructure/data/validation';
import { ErrorNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import * as navigate from 'app/area/shared/navigation';
import { isForbiddenStatus, blockAccess, isNotFoundStatus } from 'app/area/shared/actions-utils';
import { mapExternalUrlFromWire, mapExternalUrlToWire } from './utils/map-external-url';
import { externalUrlAdminTranslation } from '../constants';
import {
  RequestLanguageOptions, ReceiveLanguageOptions, FailLanguageOptions,
  RequestExternalUrl, ReceiveExternalUrl, FailExternalUrl,
  RequestDeleteExternalUrl, ReceiveDeleteExternalUrl, FailDeleteExternalUrl,
  RequestSaveExternalUrl, ReceiveSaveExternalUrl, FailSaveExternalUrl,
  UpdateItem, ChangeLanguage, CopyLanguageToAll,
  ClearForm, UpdateValidation, ResetValidation,
  ChangeLongDescription,
  CopyLongDescriptionToAll,
  UpdateExcludedFields
} from './action-types';
import { api } from './api';
import { getBackRoute } from './route';
import { ExternalUrlItem, ExternalUrlItemValidation } from './types-store';
import { ExternalUrlActionType } from 'app/area/external-urls-administration/form/types';
import { replaceTemplateString } from 'app/infrastructure/platform';

export const actionCreators = {

  loadLanguageOptions: (): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestLanguageOptions());

      try {

        const languageOptions = await api.loadLanguageOptions();

        dispatch(new ReceiveLanguageOptions(
          languageOptions.map(option => ({
            value: option.v,
            description: option.d
          }))
        ));
      } catch (err) {

        if (err && (err.userMessages || err.message)) {
          dispatch(new ErrorNotification(err.userMessages || err.message));
        }
        dispatch(new FailLanguageOptions());
      }
    },

  createExternalUrl: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => dispatch(new ReceiveExternalUrl(mapExternalUrlFromWire({}))),

  loadExternalUrl: (id: number): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestExternalUrl());

      try {

        const externalUrl = await api.loadExternalUrl(id);
        const externalUrlItem = mapExternalUrlFromWire(externalUrl);

        dispatch(new ReceiveExternalUrl(externalUrlItem));

      } catch (err) {

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.externalUrlAdministration,
            translationId: externalUrlAdminTranslation.no_access_to_external_urls
          });
        } else {

          const errorMessage = err && (err.userMessages || err.message);
          !!errorMessage && dispatch(new ErrorNotification(errorMessage));

          navigate.to({ pathname: getBackRoute() });
        }

        dispatch(new FailExternalUrl());
      }
    },

  deleteExternalUrl: (id: number, externalUrlText: string, successMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestDeleteExternalUrl());

      try {
        await api.deleteExternalUrl(id);

        navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });

        dispatch(new ReceiveDeleteExternalUrl());
        dispatch(new SuccessNotification(replaceTemplateString(successMessage, externalUrlText)));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailDeleteExternalUrl());

        if (isNotFoundStatus(err)) {
          navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
        }
      }
    },

  saveExternalUrl: (
    actionType: ExternalUrlActionType,
    externalUrl: ExternalUrlItem,
    successMessage: string,
    validationErrorMessage: string
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      const id = externalUrl?.id;

      dispatch(new RequestSaveExternalUrl());

      try {
        const externalUrlWire = mapExternalUrlToWire(externalUrl);

        if (actionType === ExternalUrlActionType.edit) {
          await api.updateExternalUrl(id, externalUrlWire);
        } else {
          await api.addExternalUrl(externalUrlWire);
        }

        dispatch(new ReceiveSaveExternalUrl());

        navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
        dispatch(new SuccessNotification(successMessage));

      } catch (err) {

        dispatch(new FailSaveExternalUrl());

        if (Object.keys(err?.validationErrors ?? {}).length > 0) {
          dispatch(new ErrorNotification(validationErrorMessage));
          dispatch(new UpdateValidation(complexValidationFromServer(err.validationErrors)));
        }

        if (err?.userMessages?.length ?? 0 > 0) {
          dispatch(new ErrorNotification(err.userMessages));
        }

        if (isNotFoundStatus(err)) {
          navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
        }
      }
    },

  updateExcludedFields: (excludedFields: ComplexValue<number>[]): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ResetValidation('excludeFields'));
    dispatch(new UpdateExcludedFields(excludedFields));
  },

  clearForm: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ClearForm());
    },

  updateItem: (item: Partial<ExternalUrlItem>): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateItem(item));
    },

  changeLanguage: (languageId: number, text: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ChangeLanguage(languageId, text));
    },

  copyLanguageToAll: (languageId: number, successMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new CopyLanguageToAll(languageId));
      dispatch(new SuccessNotification(successMessage));
    },

  changeLongDescription: (languageId: number, text: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ChangeLongDescription(languageId, text));
    },

  copyLongDescriptionToAll: (languageId: number, successMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new CopyLongDescriptionToAll(languageId));
      dispatch(new SuccessNotification(successMessage));
    },

  updateValidation: (validation: ExternalUrlItemValidation): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateValidation(validation));
    },

  resetValidation: (key: string): ActionDispatching<AnyAction, ApplicationState> => (dispatch: any, getState: any) => {
    dispatch(new ResetValidation(key));
  },

  displayError: (errorMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ErrorNotification(errorMessage));
    }
};
